import { ErrorResponse } from "../models/api/errorResponse";
import { baseApi } from "./baseApi";
import { PaginatedList } from "../models/api/paginatedList";
import { NotificationSummaryResponse } from "../models/api/notifications/notificationSummaryResponse";
import { NotificationResponse } from "../models/api/notifications/notificationResponse";
import { AdminNotificationResponse } from "../models/api/notifications/adminNotificationResponse";
import { UnreadCountResponse } from "../models/api/notifications/unreadCountResponse";
import { CreateNotificationRequest } from "../models/api/notifications/createNotificationRequest";
import { UpdateNotificationRequest } from "../models/api/notifications/updateNotificationRequest";
import { NotificationsQueryParams } from "../models/api/notifications/notificationsQueryParams";
import { PaginatedQueryParams } from "../models/api/common/paginatedQueryParams";
import { ResponseResult } from "../models/api/responseResult";
import { queryParams } from "../constants/queryParams";
import store from "../store";
import { i18n } from "../../i18n/i18n";

let previousUnreadCount: UnreadCountResponse | undefined;

i18n.on("languageChanged", () => {
    store.dispatch(baseApi.util.invalidateTags(["notifications", "notificationsUnread"]));
});

export const notificationsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getNotifications: builder.query<PaginatedList<NotificationSummaryResponse>, NotificationsQueryParams>({
            query: ({ pageNumber = 1, pageSize = 10, type, search }) => {
                const params = new URLSearchParams();
                params.set(queryParams.pageNumber, pageNumber.toString());
                params.set(queryParams.pageSize, pageSize.toString());
                if (type != null) {
                    params.set(queryParams.type, type.toString());
                }
                if (search) {
                    params.set(queryParams.search, search);
                }
                return `${import.meta.env.VITE_NOTIFICATIONS}?${params.toString()}`;
            },
            providesTags: ["notifications"],
        }),
        getNotificationById: builder.query<NotificationResponse, string>({
            query: (id) => `${import.meta.env.VITE_NOTIFICATIONS}/${id}`,
            providesTags: ["notifications"],
        }),
        getUnreadCount: builder.query<UnreadCountResponse, void>({
            query: () => `${import.meta.env.VITE_NOTIFICATIONS}/unread-count`,
            providesTags: ["notificationsUnread"],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                const { data: newCount } = await queryFulfilled;
                if (previousUnreadCount !== undefined && newCount !== previousUnreadCount) {
                    dispatch(baseApi.util.invalidateTags(["notifications"]));
                }
                previousUnreadCount = newCount;
            },
        }),
        markNotificationAsRead: builder.mutation<ResponseResult | ErrorResponse, string>({
            query: (id) => ({
                url: `${import.meta.env.VITE_NOTIFICATIONS}/${id}/read`,
                method: "PUT",
            }),
            invalidatesTags: ["notifications", "notificationsUnread"],
        }),
        markAllNotificationsAsRead: builder.mutation<ResponseResult | ErrorResponse, void>({
            query: () => ({
                url: `${import.meta.env.VITE_NOTIFICATIONS}/read-all`,
                method: "PUT",
            }),
            invalidatesTags: ["notifications", "notificationsUnread"],
        }),

        // Admin endpoints
        getAdminNotifications: builder.query<PaginatedList<AdminNotificationResponse>, PaginatedQueryParams>({
            query: ({ pageNumber = 1, pageSize = 10, search }) => {
                const params = new URLSearchParams();
                params.set(queryParams.pageNumber, pageNumber.toString());
                params.set(queryParams.pageSize, pageSize.toString());
                if (search) {
                    params.set(queryParams.search, search);
                }
                return `${import.meta.env.VITE_NOTIFICATIONS}/admin?${params.toString()}`;
            },
            providesTags: ["notifications"],
        }),
        createNotification: builder.mutation<string, CreateNotificationRequest>({
            query: (body) => ({
                url: `${import.meta.env.VITE_NOTIFICATIONS}`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["notifications", "notificationsUnread"],
        }),
        updateNotification: builder.mutation<ResponseResult | ErrorResponse, UpdateNotificationRequest>({
            query: (body) => ({
                url: `${import.meta.env.VITE_NOTIFICATIONS}/${body.id}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["notifications", "notificationsUnread"],
        }),
        deleteNotification: builder.mutation<ResponseResult | ErrorResponse, string>({
            query: (id) => ({
                url: `${import.meta.env.VITE_NOTIFICATIONS}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["notifications", "notificationsUnread"],
        }),
    }),
});

export const {
    useGetNotificationsQuery,
    useGetNotificationByIdQuery,
    useGetUnreadCountQuery,
    useMarkNotificationAsReadMutation,
    useMarkAllNotificationsAsReadMutation,
    useGetAdminNotificationsQuery,
    useCreateNotificationMutation,
    useUpdateNotificationMutation,
    useDeleteNotificationMutation,
} = notificationsApi;
